import type { ContourTexture } from '~/engine/data/structure/ContourTexture';

/** Values interpolated into an operation's success message. */
type OperationSuccessValues = Record<string, string | number>;

/**
 * What running an ROI operation produced. Tagged rather than "maybe a
 * texture" because two of the three are successes: 'unchanged' must reach
 * the user as a green toast, not a red one.
 *
 * Built once per run and read by both the preview and the commit, so the
 * two cannot report different things.
 */
export type OperationOutcome =
    /** Produced a new texture; the consumer takes ownership of it. */
    | {
          status: 'changed';
          texture: ContourTexture;
          successValues?: OperationSuccessValues;
      }
    /** Ran; nothing to change. ROI untouched, no undo entry, still reported. */
    | { status: 'unchanged'; successValues?: OperationSuccessValues }
    /** Could not run: GL guard busy, no SDF, or it threw. */
    | { status: 'failed' };

/**
 * Carry an engine outcome across to the panel's, attaching the values its
 * toast interpolates. The engine reports whether it changed anything; only
 * this layer knows what to say about it.
 */
export function toOperationOutcome(
    engineOutcome:
        | { status: 'changed'; texture: ContourTexture }
        | { status: 'unchanged' },
    successValues: OperationSuccessValues
): OperationOutcome {
    return engineOutcome.status === 'changed'
        ? { status: 'changed', texture: engineOutcome.texture, successValues }
        : { status: 'unchanged', successValues };
}
